import React, { useEffect, useMemo, useState } from 'react';
import {


  Alert, Box, Button, Chip, Divider, Grid, LinearProgress, List, ListItem,
  ListItemText, Paper, Skeleton, Stack, Typography,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Switch, FormControlLabel
} from '@mui/material';
import {
  EmojiEvents, EventAvailable, Groups, MonetizationOn, Refresh,
  Add, Edit, Delete, MoreVert
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer,
  Tooltip as RechartTooltip, XAxis, YAxis,
} from 'recharts';
import { challengesAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';

const COLORS = {
  ink: '#111827',
  muted: '#6B7280',
  border: '#E5E7EB',
  blue: '#2563EB',
  green: '#16A34A',
  amber: '#D97706',
  cyan: '#0891B2',
  red: '#DC2626',
  violet: '#7C3AED',
};

const STATUS_COLORS = {
  active: COLORS.green,
  upcoming: COLORS.blue,
  completed: COLORS.violet,
  cancelled: COLORS.red,
};

function formatNumber(value) {
  if (typeof value !== 'number') return value ?? '-';
  return value.toLocaleString('en-IN');
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function MetricCard({ title, value, caption, icon, color, loading }) {
  return (
    <Paper elevation={0} sx={{ p: 2, height: '100%', border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
      <Stack spacing={1.5}>
        <Box sx={{ width: 38, height: 38, borderRadius: 1.25, bgcolor: `${color}14`, display: 'grid', placeItems: 'center' }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
        {loading ? (
          <Box>
            <Skeleton width={92} height={34} />
            <Skeleton width={150} height={16} />
          </Box>
        ) : (
          <Box>
            <Typography sx={{ fontSize: 27, lineHeight: 1, fontWeight: 850, color: COLORS.ink }}>
              {value}
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 13, fontWeight: 800, color: COLORS.ink }}>
              {title}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 12, color: COLORS.muted }}>
              {caption}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <Paper elevation={0} sx={{ p: 2.25, border: 1, borderColor: 'divider', borderRadius: 1.5, height: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 850, color: COLORS.ink }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {children}
    </Paper>
  );
}

function EmptyState({ text }) {
  return (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <EmojiEvents sx={{ color: 'text.disabled', fontSize: 38, mb: 1 }} />
      <Typography sx={{ fontSize: 13, color: COLORS.muted }}>{text}</Typography>
    </Box>
  );
}

function ProgramsTab() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({
    title: '', type: 'challenge', description: '',
    startDate: '', endDate: '',
    metric: 'check_ins', targetValue: '',
    location: '', capacity: '', isPaid: false, price: ''
  });

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const res = await challengesAPI.getAll();
      setPrograms(res.data.data || []);
    } catch (err) {
      setError('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPrograms(); }, []);

  const handleOpen = (prog = null) => {
    if (prog) {
      setEditId(prog._id);
      setFormData({
        title: prog.title || '',
        type: prog.type || 'challenge',
        description: prog.description || '',
        startDate: prog.startDate ? prog.startDate.split('T')[0] : '',
        endDate: prog.endDate ? prog.endDate.split('T')[0] : '',
        metric: prog.metric || 'check_ins',
        targetValue: prog.targetValue || '',
        location: prog.location || '',
        capacity: prog.capacity || '',
        isPaid: prog.isPaid || false,
        price: prog.price || '',
      });
    } else {
      setEditId(null);
      setFormData({
        title: '', type: 'challenge', description: '',
        startDate: '', endDate: '',
        metric: 'check_ins', targetValue: '',
        location: '', capacity: '', isPaid: false, price: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await challengesAPI.update(editId, formData);
      } else {
        await challengesAPI.create(formData);
      }
      handleClose();
      loadPrograms();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving program');
    }
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ open: true, id });
  };

  const doDelete = async () => {
    try {
      await challengesAPI.delete(deleteConfirm.id);
      loadPrograms();
    } catch (err) {
      setError('Error deleting program');
    } finally {
      setDeleteConfirm({ open: false, id: null });
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Manage Programs</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Add Program
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead sx={{ bgcolor: 'background.paper' }}>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Enrollments</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><Skeleton /></TableCell></TableRow>
            ) : programs.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No programs found.</TableCell></TableRow>
            ) : programs.map(p => (
              <TableRow key={p._id}>
                <TableCell sx={{ fontWeight: 600 }}>{p.title}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{p.type}</TableCell>
                <TableCell>
                  <Chip size="small" label={p.status} sx={{ textTransform: 'capitalize' }} />
                </TableCell>
                <TableCell>{new Date(p.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} - {new Date(p.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                <TableCell>{p.enrollmentCount}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpen(p)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(p._id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Program' : 'New Program'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" required fullWidth value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
            <TextField label="Description" fullWidth multiline rows={2} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select value={formData.type} label="Type" onChange={e => setFormData({ ...formData, type: e.target.value })}>
                  <MenuItem value="challenge">Challenge</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Start Date" type="date" required fullWidth InputLabelProps={{ shrink: true }} value={formData.startDate} onChange={e => setFormData({ ...formData, startDate: e.target.value })} />
              <TextField label="End Date" type="date" required fullWidth InputLabelProps={{ shrink: true }} value={formData.endDate} onChange={e => setFormData({ ...formData, endDate: e.target.value })} />
            </Stack>

            {formData.type === 'challenge' && (
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Metric</InputLabel>
                  <Select value={formData.metric} label="Metric" onChange={e => setFormData({ ...formData, metric: e.target.value })}>
                    <MenuItem value="check_ins">Check-ins</MenuItem>
                    <MenuItem value="calories_burned">Calories Burned</MenuItem>
                    <MenuItem value="workouts_completed">Workouts</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Target Value" type="number" fullWidth value={formData.targetValue} onChange={e => setFormData({ ...formData, targetValue: e.target.value })} />
              </Stack>
            )}

            {formData.type === 'event' && (
              <>
                <Stack direction="row" spacing={2}>
                  <TextField label="Location" fullWidth value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} />
                  <TextField label="Capacity" type="number" fullWidth value={formData.capacity} onChange={e => setFormData({ ...formData, capacity: e.target.value })} />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControlLabel control={<Switch checked={formData.isPaid} onChange={e => setFormData({ ...formData, isPaid: e.target.checked })} />} label="Paid Event" />
                  {formData.isPaid && <TextField label="Price (INR)" type="number" fullWidth value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} />}
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Program"
        message="This will permanently delete this program and all its data."
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
      />
    </Box>
  );
}
export default function ChallengesEvents() {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await challengesAPI.getAdminStats();
      setData(res.data.data || {});
    } catch (err) {
      console.error('Challenge metrics load error:', err);
      setError(err.response?.data?.message || 'Could not load challenge metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  const summary = data?.summary || {};
  const statusData = useMemo(() => (
    Object.entries(data?.statusBreakdown || {})
      .map(([status, count]) => ({ status, count }))
      .filter(item => item.count > 0)
  ), [data]);

  const topData = useMemo(() => (
    (data?.topChallenges || []).map(item => ({
      name: item.title?.length > 18 ? `${item.title.slice(0, 18)}...` : item.title,
      enrollments: item.enrollmentCount || 0,
      type: item.type,
    }))
  ), [data]);

  return (
    <Box>
      <PageHeader
        title="Challenges & Events"
        subtitle="Manage gym challenges, view enrollments, and track analytics."
        gradient="linear-gradient(90deg, #D97706, #FBBF24)"
        actions={tab === 0 && (
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadStats} disabled={loading}>Refresh</Button>
        )}
      />

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Dashboard" />
          <Tab label="Manage Programs" />
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {tab === 0 && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Total Programs"
                value={formatNumber((summary.totalChallenges || 0) + (summary.totalEvents || 0))}
                caption={`${formatNumber(summary.totalChallenges || 0)} challenges, ${formatNumber(summary.totalEvents || 0)} events`}
                icon={<EmojiEvents />}
                color={COLORS.blue}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Active Now"
                value={formatNumber(summary.totalActive || 0)}
                caption={`${formatNumber(summary.totalUpcoming || 0)} upcoming`}
                icon={<EventAvailable />}
                color={COLORS.green}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Enrollments"
                value={formatNumber(summary.totalEnrollments || 0)}
                caption={`${formatNumber(summary.totalCompletions || 0)} completed`}
                icon={<Groups />}
                color={COLORS.cyan}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Paid Event Potential"
                value={formatCurrency(summary.potentialRevenue || 0)}
                caption={`${formatNumber(summary.paidEventsSold || 0)} seats sold across ${formatNumber(summary.paidEventCount || 0)} paid events`}
                icon={<MonetizationOn />}
                color={COLORS.amber}
                loading={loading}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <Panel title="Completion Rate" subtitle="Completed enrollments as a share of active sold seats">
                {loading ? (
                  <Skeleton height={168} />
                ) : (
                  <Stack spacing={2.5}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: COLORS.ink }}>Overall completion</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 850, color: COLORS.green }}>
                          {formatNumber(summary.overallCompletionRate || 0)}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, summary.overallCompletionRate || 0)}
                        sx={{ height: 8, borderRadius: 999, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: COLORS.green, borderRadius: 999 } }}
                      />
                    </Box>
                    <Divider />
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: 22, fontWeight: 850, color: COLORS.ink }}>{formatNumber(summary.totalCompletions || 0)}</Typography>
                        <Typography sx={{ fontSize: 12, color: COLORS.muted }}>Completions</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: 22, fontWeight: 850, color: COLORS.ink }}>{formatNumber(summary.totalEnrollments || 0)}</Typography>
                        <Typography sx={{ fontSize: 12, color: COLORS.muted }}>Valid enrollments</Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                )}
              </Panel>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Panel title="Status Breakdown" subtitle="Current lifecycle mix">
                {loading ? (
                  <Skeleton height={230} />
                ) : statusData.length === 0 ? (
                  <EmptyState text="No challenge or event status data yet" />
                ) : (
                  <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={statusData} dataKey="count" nameKey="status" innerRadius={54} outerRadius={86} paddingAngle={3}>
                        {statusData.map(entry => (
                          <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || COLORS.muted} />
                        ))}
                      </Pie>
                      <RechartTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Panel>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Panel title="Top By Enrollment" subtitle="Highest participating challenges and events">
                {loading ? (
                  <Skeleton height={230} />
                ) : topData.length === 0 ? (
                  <EmptyState text="No enrollments captured yet" />
                ) : (
                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={topData} layout="vertical" margin={{ top: 5, right: 18, left: 6, bottom: 0 }}>
                      <CartesianGrid stroke="#EEF0F4" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12, fill: COLORS.muted }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={118} tick={{ fontSize: 12, fill: COLORS.muted }} axisLine={false} tickLine={false} />
                      <RechartTooltip cursor={{ fill: '#F8FAFC' }} />
                      <Bar dataKey="enrollments" name="Enrollments" radius={[0, 5, 5, 0]} fill={COLORS.blue} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Panel>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Panel title="Best Completion Programs" subtitle="Completion rate by enrolled program">
                {loading ? (
                  <Stack spacing={1.5}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={42} />)}</Stack>
                ) : (data?.completionBreakdown || []).length === 0 ? (
                  <EmptyState text="No completion data available yet" />
                ) : (
                  <Stack spacing={1.5}>
                    {(data?.completionBreakdown || []).map(item => (
                      <Box key={item._id}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: COLORS.ink }} noWrap>{item.title}</Typography>
                            <Typography sx={{ fontSize: 12, color: COLORS.muted }}>{formatNumber(item.enrollmentCount || 0)} enrollments</Typography>
                          </Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 850, color: COLORS.green }}>
                            {Math.round(item.completionRate || 0)}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, item.completionRate || 0)}
                          sx={{ height: 7, borderRadius: 999, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: COLORS.green, borderRadius: 999 } }}
                        />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Panel>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Panel title="Recent Enrollments" subtitle="Latest member joins across challenges and events">
                {loading ? (
                  <Stack spacing={1}>{[...Array(6)].map((_, i) => <Skeleton key={i} height={48} />)}</Stack>
                ) : (data?.recentEnrollments || []).length === 0 ? (
                  <EmptyState text="No recent enrollments yet" />
                ) : (
                  <List disablePadding>
                    {(data?.recentEnrollments || []).map((item, index) => (
                      <React.Fragment key={item._id}>
                        <ListItem disablePadding sx={{ py: 1 }}>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontSize: 13, fontWeight: 850, color: COLORS.ink }} noWrap>
                                  {item.userId?.name || item.userId?.email || 'Unknown member'}
                                </Typography>
                                <Chip
                                  label={item.status}
                                  size="small"
                                  sx={{ bgcolor: item.status === 'completed' ? '#F0FDF4' : '#F3F4F6', color: item.status === 'completed' ? COLORS.green : COLORS.muted }}
                                />
                              </Stack>
                            }
                            secondary={
                              <Typography sx={{ fontSize: 12, color: COLORS.muted }} noWrap>
                                {item.challengeId?.title || 'Deleted program'} - {formatDate(item.enrolledAt)} - progress {formatNumber(item.progress || 0)}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < (data?.recentEnrollments || []).length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Panel>
            </Grid>
          </Grid>
        </Box>
      )}
      {tab === 1 && <ProgramsTab />}
    </Box>
  );
}
